import { writable, get } from 'svelte/store';
import { writable as storedWritable } from '../stores/_localStore.js';
import { v4 as uuidv4 } from 'uuid';
import gql from 'graphql-tag';
import { client } from 'utils/apollo.js';
import determineProductColourImageUrl from '../utils/determineProductColourImageUrl.js';

// DESIGN STATE STORE
// https://stackoverflow.com/questions/67799289/questions-about-using-svelte-stores-with-tree-like-nested-object
const _designState = storedWritable('designState', []);

const _designStateCopy = storedWritable('designStateCopy', []);

// designId is the konva stage id from the layout
function addNewDesign(stageId) {
  _designState.update((x) => {
    x.push({
      id: stageId,
      mode: 'design',
      cartItemId: null,
      top: {
        product: null,
        colour: null,
        sublimationTemplate: null,
        teamDetails: [],
        designElements: {
          front: [],
          back: [],
          rightSide: [],
          leftSide: [],
        },
        sponsorship: false,
      },

      bottom: {
        product: null,
        colour: null,
        sublimationTemplate: null,
        teamDetails: [],
        designElements: {
          front: [],
          back: [],
          rightSide: [],
          leftSide: [],
        },
        sponsorship: false,
      },

      hat: {
        product: null,
        colour: null,
        sublimationTemplate: null,
        teamDetails: [],
        designElements: {
          front: [],
          back: [],
          rightSide: [],
          leftSide: [],
        },
        sponsorship: false,
      },

      socks: {
        product: null,
        colour: null,
        sublimationTemplate: null,
        teamDetails: [],
        designElements: {
          front: [],
          back: [],
          rightSide: [],
          leftSide: [],
        },
        sponsorship: false,
      },

      // wristband, arm sleeves, headband
      accessory: {
        product: null,
        colour: null,
        sublimationTemplate: null,
        teamDetails: [],
        designElements: {
          front: [],
          back: [],
          rightSide: [],
          leftSide: [],
        },
        sponsorship: false,
      },
    });
    return x;
  });
}

function clearDesignState() {
  _designState.set([]);
}

// Select design object from array by id and return index
// The original intent of this was to allow multiple designs in the state
// However, we currently only allow 1 design in the state
function determineDesignIndex(designId) {
  const storeValue = get(_designState) || [];
  const index = storeValue.findIndex((o) => o.id === designId);
  if (index === -1) {
    console.log('design not found');
  }
  return index;
}

function determineElementIndex(elementArray, elementId) {
  const index = elementArray.findIndex((o) => o.id === elementId);
  return index;
}

// Add product and selected colour to state
function addProduct(designId, product, colour, selectedSublimationTemplate, overrideId = false) {
  // overrideId is used to override the id of the design state
  // Used when the stage id from layout doesn't match the design id
  let index;
  if (overrideId) {
    index = 0; // TODO - this only works since we currently only allow 1 item in design state
  } else {
    index = determineDesignIndex(designId);
  }
  const productType = product.type;
  _designState.update((x) => {
    x[index].id = designId; // Update the id to match the stage id
    x[index][productType].product = {
      id: product.id,
      slug: product.slug,
      rootSlug: product.rootSlug,
      name: product.name,
      price: product.price,
      category: product.category,
      collection: product.collection,
      isModelBasedImages: product.isModelBasedImages,
    };
    x[index][productType].colour = colour;
    x[index][productType].sublimationTemplate = selectedSublimationTemplate;
    return x;
  });
}

function deleteProduct(designId, productType) {
  if (typeof window === 'undefined') {
    return null;
  }
  const index = determineDesignIndex(designId);
  _designState.update((x) => {
    x[index][productType] = {
      product: null,
      productPrice: null,
      colour: null,
      teamDetails: [],
      designElements: {
        front: [],
        back: [],
        rightSide: [],
        leftSide: [],
      },
      sponsorship: false,
    };
    return x;
  });
}

function updateProductColour(designId, product, colour) {
  const index = determineDesignIndex(designId);
  const productType = product.type;
  _designState.update((x) => {
    x[index][productType].colour = colour;
    return x;
  });
}

function updateProductSublimationTemplate(designId, product, template) {
  const index = determineDesignIndex(designId);
  const productType = product.type;
  _designState.update((x) => {
    x[index][productType].sublimationTemplate = template;
    return x;
  });
}

function getSelectedProductColour(designId, type) {
  let colourId;
  const index = determineDesignIndex(designId);
  // only run on client - if no index found will return -1
  if (index !== -1) {
    const storeValue = get(_designState);
    colourId = storeValue[index][type].colour.id;
  }
  return colourId;
}

// Add design element to appropriate design, type, side
function addDesignElement(designId, productType, side, props) {
  const index = determineDesignIndex(designId);

  // Check if user has added sponsorship to design
  let sponsorship;
  if (props.sponsorship) {
    sponsorship = true;
  } else {
    sponsorship = false;
  }

  _designState.update((x) => {
    x[index][productType].designElements[side].push(props);
    x[index][productType].sponsorship = sponsorship;
    return x;
  });
}

function updateDesignElementPosition(designId, elementId, productType, side, props) {
  const designIndex = determineDesignIndex(designId);
  const storeValue = get(_designState);
  // const side = determineSide(layerName)
  const elementArray = storeValue[designIndex][productType].designElements[side];
  if (!elementArray) {
    return;
  }
  const elementIndex = determineElementIndex(elementArray, elementId);
  const designElement = storeValue[designIndex][productType].designElements[side][elementIndex];

  const updatedElement = Object.assign({}, designElement, props); // merges two objcets and overwrites if duplicate keys - e.g. update

  _designState.update((x) => {
    x[designIndex][productType].designElements[side][elementIndex] = updatedElement;
    return x;
  });
}

function designElementsExist() {
  let elementsExist = false;
  // const designIndex = determineDesignIndex(designId)
  const designIndex = 0; // TODO - we will only check the first entry in the design state - This is because we currently only allow 1 item in design state
  if (designIndex !== -1) {
    const storeValue = get(_designState);
    const topElements = storeValue[designIndex]?.top?.designElements;
    if (
      topElements?.front.length > 0 ||
      topElements?.back.length > 0 ||
      topElements.rightSide.length > 0 ||
      topElements.leftSide.length > 0
    ) {
      elementsExist = true;
    }
    const bottomElements = storeValue[designIndex]?.bottom?.designElements;
    if (
      bottomElements.front.length > 0 ||
      bottomElements.back.length > 0 ||
      bottomElements.rightSide.length > 0 ||
      bottomElements.leftSide.length > 0
    ) {
      elementsExist = true;
    }
    const hatElements = storeValue[designIndex]?.hat?.designElements;
    if (
      hatElements.front.length > 0 ||
      hatElements.back.length > 0 ||
      hatElements.rightSide.length > 0 ||
      hatElements.leftSide.length > 0
    ) {
      elementsExist = true;
    }
    const socksElements = storeValue[designIndex]?.socks?.designElements;
    if (
      socksElements.front.length > 0 ||
      socksElements.back.length > 0 ||
      socksElements.rightSide.length > 0 ||
      socksElements.leftSide.length > 0
    ) {
      elementsExist = true;
    }
    const accessoryElements = storeValue[designIndex]?.accessory?.designElements;
    if (
      accessoryElements.front.length > 0 ||
      accessoryElements.back.length > 0 ||
      accessoryElements.rightSide.length > 0 ||
      accessoryElements.leftSide.length > 0
    ) {
      elementsExist = true;
    }
  }

  return elementsExist;
}

function updateDesignPreview(designId, productType, side, dataURL) {
  const designIndex = determineDesignIndex(designId);
  // const side = determineSide(layerName)
  _designState.update((x) => {
    x[designIndex][productType][side + 'Preview'] = dataURL;
    return x;
  });
}

function deleteDesignElement(designId, productType, side, elementId) {
  const storeValue = get(_designState);
  const designIndex = determineDesignIndex(designId);
  // const side = determineSide(layerName)
  const elementArray = storeValue[designIndex][productType].designElements[side];
  if (!elementArray) {
    return;
  }
  const elementIndex = determineElementIndex(elementArray, elementId);

  // Now splice element out of array
  elementArray.splice(elementIndex, 1);

  _designState.update((x) => {
    x[designIndex][productType].designElements[side] = elementArray;
    return x;
  });
}

function removeAllDesignElements(designId, productType) {
  const designIndex = determineDesignIndex(designId);

  _designState.update((x) => {
    x[designIndex][productType].designElements.front = [];
    x[designIndex][productType].designElements.back = [];
    x[designIndex][productType].designElements.leftSide = [];
    x[designIndex][productType].designElements.rightSide = [];
    // remove sponsorship as well
    x[designIndex][productType].sponsorship = false;
    return x;
  });
}

function updateElementOrder(designId, productType, side, elementId, initialIndex, newIndex) {
  const storeValue = get(_designState);
  const designIndex = determineDesignIndex(designId);
  // const side = determineSide(layerName)
  const elementArray = storeValue[designIndex][productType].designElements[side];
  if (!elementArray) {
    return;
  }
  const elementIndex = determineElementIndex(elementArray, elementId); // should match initialIndex?

  // Now splice element out of array
  const movedElement = elementArray.splice(elementIndex, 1); // returns an array

  // Now insert it back at new index
  // TODO - should we update the element zIndex value as well?
  elementArray.splice(newIndex, 0, movedElement[0]);

  _designState.update((x) => {
    x[designIndex][productType].designElements[side] = elementArray;
    return x;
  });
}

function updateDesignElement(designId, elementId, side, props) {
  const designIndex = determineDesignIndex(designId);
  const storeValue = get(_designState);
  // const side = determineSide(layerName)
  const elementArray = storeValue[designIndex][props.productType].designElements[side];
  if (!elementArray) {
    return;
  }
  const elementIndex = determineElementIndex(elementArray, elementId);
  const designElement = storeValue[designIndex][props.productType].designElements[side][elementIndex];

  const updatedElement = Object.assign({}, designElement, props); // merges two objcets and overwrites if duplicate keys - e.g. update

  // Check if user has added sponsorship to design
  let sponsorship;
  if (props.sponsorship) {
    sponsorship = true;
  } else {
    sponsorship = false;
  }

  _designState.update((x) => {
    x[designIndex][props.productType].designElements[side][elementIndex] = updatedElement;
    x[designIndex][props.productType].sponsorship = sponsorship;
    return x;
  });
}

function getElementDetailsbyId(designId, productType, side, elementId) {
  const storeValue = get(_designState);
  const designIndex = determineDesignIndex(designId);
  // const side = determineSide(layerName)
  const elementArray = storeValue[designIndex][productType].designElements[side];
  if (!elementArray) {
    return;
  }
  const elementIndex = determineElementIndex(elementArray, elementId); // should match initialIndex?
  const element = elementArray[elementIndex];
  return element;
}

function arrayOfSelectedProducts(designId) {
  if (typeof window === 'undefined') {
    return null;
  }
  const storeValue = get(_designState);
  const designIndex = determineDesignIndex(designId);
  const productsArray = [];
  const designData = storeValue[designIndex];
  if (designData?.top?.product) {
    productsArray.push('top');
  }
  if (designData?.bottom?.product) {
    productsArray.push('bottom');
  }
  if (designData?.hat?.product) {
    productsArray.push('hat');
  }
  if (designData?.socks?.product) {
    productsArray.push('socks');
  }
  if (designData?.accessory?.product) {
    productsArray.push('accessory');
  }

  return productsArray;
}

function isNumberInputRequired(designId, arrayOfSelectedProducts) {
  const storeValue = get(_designState);
  const designIndex = determineDesignIndex(designId);
  const numberInputRequired = false;

  for (const product of arrayOfSelectedProducts) {
    const numberInputFront = storeValue[designIndex][product].designElements.front.some(
      (e) => e.type === 'name and number' || e.type === 'number',
    );
    if (numberInputFront) {
      return true;
    }
    const numberInputBack = storeValue[designIndex][product].designElements.back.some(
      (e) => e.type === 'name and number' || e.type === 'number',
    );
    if (numberInputBack) {
      return true;
    }
    const numberInputLeftSide = storeValue[designIndex][product].designElements.leftSide.some(
      (e) => e.type === 'name and number' || e.type === 'number',
    );
    if (numberInputLeftSide) {
      return true;
    }
    const numberInputRightSide = storeValue[designIndex][product].designElements.rightSide.some(
      (e) => e.type === 'name and number' || e.type === 'number',
    );
    if (numberInputRightSide) {
      return true;
    }
  }
  return numberInputRequired;
}

function isNameInputRequired(designId, arrayOfSelectedProducts) {
  const storeValue = get(_designState);
  const designIndex = determineDesignIndex(designId);
  const nameInputRequired = false;

  for (const product of arrayOfSelectedProducts) {
    const nameInputFront = storeValue[designIndex][product].designElements.front.some(
      (e) => e.type === 'name and number' || e.type === 'name',
    );
    if (nameInputFront) {
      return true;
    }
    const nameInputBack = storeValue[designIndex][product].designElements.back.some(
      (e) => e.type === 'name and number' || e.type === 'name',
    );
    if (nameInputBack) {
      return true;
    }
    const nameInputLeftSide = storeValue[designIndex][product].designElements.leftSide.some(
      (e) => e.type === 'name and number' || e.type === 'name',
    );
    if (nameInputLeftSide) {
      return true;
    }
    const nameInputRightSide = storeValue[designIndex][product].designElements.rightSide.some(
      (e) => e.type === 'name and number' || e.type === 'name',
    );
    if (nameInputRightSide) {
      return true;
    }
  }
  return nameInputRequired;
}

async function getProductBackgroundImageUrl({
  designId,
  productType,
  side,
  showModel,
  gender = 'mens',
  size = '256',
  isModelBasedImages = true,
}) {
  const designIndex = determineDesignIndex(designId);

  // only run on client - if no index found will return -1
  if (designIndex === -1) {
    return;
  }

  const storeValue = get(_designState);
  const productRootSlug = storeValue[designIndex][productType]?.product?.rootSlug;
  const productSlug = storeValue[designIndex][productType]?.product?.slug;
  const colourObject = storeValue[designIndex][productType]?.colour;
  const colourPathName = colourObject ? colourObject.pathName : null;
  const sublimationTemplateId = storeValue[designIndex][productType]?.sublimationTemplate?.SublimationTemplate?.id;

  return await determineProductColourImageUrl({
    productSlug: productRootSlug ? productRootSlug : productSlug,
    colourPathName,
    colourObject,
    imageSize: size,
    imageType: 'png',
    gender,
    side,
    showModel,
    isModelBasedImages,
    sublimationTemplateId,
  });
}

// TODO - This does not allow multiple designs to be saved in state
function copyDesignState(designId) {
  // const designIndex = determineDesignIndex(designId)
  const storeValue = get(_designState);
  _designStateCopy.set(storeValue);
}

function restoreDesignState() {
  const storeValue = get(_designStateCopy);
  _designState.set(storeValue);
  _designStateCopy.set(null);
  if (typeof window !== 'undefined') {
    window.localStorage.removeItem('designStateCopy');
  }
}

function loadDesignState(designState, mode = null, cartItemId = null) {
  _designState.set([designState]); // designState is an array

  if (mode === 'edit') {
    const designIndex = determineDesignIndex(designState.id);
    _designState.update((x) => {
      x[designIndex].mode = 'edit';
      x[designIndex].cartItemId = cartItemId;
      return x;
    });
  }

  if (mode === 'db-edit') {
    const designIndex = determineDesignIndex(designState.id);
    _designState.update((x) => {
      x[designIndex].mode = 'db-edit';
      x[designIndex].cartItemId = cartItemId;
      return x;
    });
  }

  if (mode === 'design') {
    const designIndex = determineDesignIndex(designState.id);
    _designState.update((x) => {
      x[designIndex].mode = 'design';
      return x;
    });
  }
}

function getDesignMode(designId) {
  const designIndex = determineDesignIndex(designId);
  const storeValue = get(_designState);
  return storeValue[designIndex]?.mode;
}

function getDesignCartItemId(designId) {
  const designIndex = determineDesignIndex(designId);
  const storeValue = get(_designState);
  return storeValue[designIndex]?.cartItemId;
}

function getDesignId() {
  // TODO - this doesn't support multiple saved designs
  const storeValue = get(_designState);
  if (storeValue.length > 0) {
    return storeValue[0].id;
  } else {
    return null;
  }
}

async function generateDesignPreview({ designId, item, side, gender, name, number, isModelBasedImages = true }) {
  const designIndex = determineDesignIndex(designId);
  if (designIndex === -1) {
    return;
  }
  const storeValue = get(_designState);

  const designElements = storeValue[designIndex][item].designElements[side];

  const backgroundImageUrl = await getProductBackgroundImageUrl({
    designId,
    productType: item,
    side,
    showModel: true,
    gender,
    isModelBasedImages,
  });

  const DESIGNPREVIEW = gql`
    query MyQuery($designElements: jsonb!, $backgroundImageUrl: String, $name: String, $number: String) @cached {
      designPreview(
        designElements: $designElements
        backgroundImageUrl: $backgroundImageUrl
        name: $name
        number: $number
      ) {
        image
      }
    }
  `;

  const res = await client.query({
    query: DESIGNPREVIEW,
    variables: {
      backgroundImageUrl,
      designElements: JSON.stringify(designElements),
      name,
      number,
    },
  });
  const data = await res.data;
  return data.designPreview.image;
}

async function saveExists(designId) {
  const response = await client.query({
    query: gql`
      query MyQuery($_eq: uuid!) {
        SavedDesigns(where: { designId: { _eq: $_eq } }, order_by: { updated_at: desc }) {
          saveName
          identifier
          previewImage
          userEmail
        }
      }
    `,
    variables: {
      _eq: designId,
    },
  });

  if (response.data.SavedDesigns.length > 0) {
    const data = await response.data.SavedDesigns[0]; // Return the latest save
    return {
      saveName: data.saveName,
      identifier: data.identifier,
      previewImage: data.previewImage,
      userEmail: data.userEmail,
    };
  } else {
    return null;
  }
}
function stateIsEmpty() {
  const storeValue = get(_designState);
  let isEmpty = true;
  if (storeValue.length > 0) {
    if (storeValue[0].top.product !== null) {
      isEmpty = false;
    }
    if (storeValue[0].bottom.product !== null) {
      isEmpty = false;
    }
    if (storeValue[0].hat.product !== null) {
      isEmpty = false;
    }
    if (storeValue[0].socks.product !== null) {
      isEmpty = false;
    }
    if (storeValue[0].accessory.product !== null) {
      isEmpty = false;
    }

    return isEmpty;
  } else {
    return true;
  }
}

async function saveDesign({
  designId,
  teamDetails,
  gender,
  arrayOfSelectedProducts,
  saveName,
  userEmail,
  identifier = null,
}) {
  const designIndex = determineDesignIndex(designId);
  const storeValue = get(_designState);

  const designState = storeValue[designIndex];

  // check which products exist and set most prominent as preview
  let productType = 'top';
  if (arrayOfSelectedProducts.includes('accessory')) {
    productType = 'accessory';
  }
  if (arrayOfSelectedProducts.includes('socks')) {
    productType = 'socks';
  }
  if (arrayOfSelectedProducts.includes('hat')) {
    productType = 'hat';
  }
  if (arrayOfSelectedProducts.includes('bottom')) {
    productType = 'bottom';
  }
  if (arrayOfSelectedProducts.includes('top')) {
    productType = 'top';
  }

  const previewImage = await generateDesignPreview({
    designId,
    item: productType,
    side: 'front',
    gender,
    isModelBasedImages: designState[productType].isModelBasedImages,
  });

  let response;
  if (identifier === null) {
    identifier = uuidv4();
    response = await client.mutate({
      mutation: gql`
        mutation MyMutation(
          $designState: jsonb!
          $identifier: uuid!
          $teamDetails: jsonb!
          $previewImage: String!
          $designId: uuid!
          $saveName: String!
          $userEmail: String!
        ) {
          insert_SavedDesigns_one(
            object: {
              designState: $designState
              identifier: $identifier
              teamDetails: $teamDetails
              previewImage: $previewImage
              designId: $designId
              saveName: $saveName
              userEmail: $userEmail
            }
          ) {
            identifier
          }
        }
      `,
      variables: {
        identifier,
        designState,
        teamDetails,
        previewImage,
        designId,
        saveName,
        userEmail,
      },
    });

    return response.data.insert_SavedDesigns_one.identifier;
  } else {
    response = await client.mutate({
      mutation: gql`
        mutation MyMutation(
          $identifier: uuid!
          $designState: jsonb!
          $previewImage: String!
          $teamDetails: jsonb!
          $designId: uuid!
        ) {
          update_SavedDesigns(
            where: { designId: { _eq: $identifier } }
            _set: {
              designState: $designState
              previewImage: $previewImage
              teamDetails: $teamDetails
              designId: $designId
            }
          ) {
            returning {
              identifier
            }
          }
        }
      `,
      variables: {
        identifier,
        designState,
        teamDetails,
        previewImage,
        designId,
      },
    });

    return response.data.update_SavedDesigns.returning[0].identifier;
  }
}

export const designState = {
  subscribe: _designState.subscribe,
  addProduct,
  deleteProduct,
  addDesignElement,
  addNewDesign,
  clearDesignState,
  updateDesignElementPosition,
  designElementsExist,
  deleteDesignElement,
  getSelectedProductColour,
  updateProductColour,
  updateProductSublimationTemplate,
  updateDesignPreview,
  updateElementOrder,
  getElementDetailsbyId,
  updateDesignElement,
  determineDesignIndex,
  arrayOfSelectedProducts,
  isNumberInputRequired,
  isNameInputRequired,
  getProductBackgroundImageUrl,
  removeAllDesignElements,
  copyDesignState,
  loadDesignState,
  getDesignMode,
  getDesignCartItemId,
  restoreDesignState,
  getDesignId,
  generateDesignPreview,
  saveDesign,
  saveExists,
  stateIsEmpty,
  // determineSide
};

export const activeElement = writable(null);

export const currentTab = writable('text');

export const editingElement = writable(null);

export const colourSelected = writable(false);

export const isTablet = writable(false);

export const isMobile = writable(false);

export const designTabActive = writable(false);

export const newProductColour = writable(null);

export const newProductSublimationTemplate = writable(null);
